import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CUSTOMER_FISCAL_PROFILES_QUERY,
  REQUEST_CFDI_MUTATION,
} from './invoicing.gql';

import type { FiscalProfile, Invoice } from './types';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface ProfilesResponse {
  customerFiscalProfiles: FiscalProfile[];
}

interface RequestCfdiDialogProps {
  sale_order_id: string;
  customer_id: string;
  on_close: () => void;
  on_done: (invoice: Invoice) => void | Promise<void>;
  on_error: (msg: string | null) => void;
}

const COMMON_USES = ['G01', 'G03', 'P01', 'D04'] as const;

export function RequestCfdiDialog({
  sale_order_id,
  customer_id,
  on_close,
  on_done,
  on_error,
}: RequestCfdiDialogProps): JSX.Element {
  const { t } = useTranslation();
  const { data, loading: loading_profiles } = useQuery<ProfilesResponse>(
    CUSTOMER_FISCAL_PROFILES_QUERY,
    {
      variables: { customer_id },
      fetchPolicy: 'network-only',
    },
  );

  const profiles = (data?.customerFiscalProfiles ?? []).filter((p) => p.status === 'active');
  const default_profile = profiles.find((p) => p.is_default) ?? profiles[0];
  const [profile_id, setProfileId] = useState<string>(default_profile?.id ?? '');
  const [cfdi_usage_override, setCfdiUsageOverride] = useState<string>('');
  const [requestCfdi, { loading: submitting }] = useMutation(REQUEST_CFDI_MUTATION);

  const can_submit = !submitting && profile_id.length > 0;

  const onClickSubmit = async (): Promise<void> => {
    if (!can_submit) return;
    on_error(null);
    const idempotency_key =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `np-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      const res = await requestCfdi({
        variables: {
          input: {
            sale_order_id,
            customer_fiscal_profile_id: profile_id,
            cfdi_usage: cfdi_usage_override.trim() || null,
          },
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        context: { headers: { 'Idempotency-Key': idempotency_key } },
      });
      const invoice = res.data?.requestCfdi as Invoice | undefined;
      if (invoice) await on_done(invoice);
    } catch (err) {
      if (err instanceof ApolloError) {
        const code = err.graphQLErrors[0]?.message ?? err.message;
        if (code === 'invoice_already_stamped') return on_error(t('invoicing.error_already_stamped'));
        if (code === 'invoice_in_progress') return on_error(t('invoicing.error_in_progress'));
        if (code === 'invoicing_issuer_missing')
          return on_error(t('invoicing.error_issuer_missing'));
        if (code === 'invoice_requires_customer')
          return on_error(t('invoicing.error_requires_customer'));
        if (code === 'invoice_fiscal_profile_customer_mismatch')
          return on_error(t('invoicing.error_profile_mismatch'));
        if (code.startsWith('invoice_sale_not_invoiceable'))
          return on_error(t('invoicing.error_sale_not_invoiceable'));
        if (code.startsWith('pac_error'))
          return on_error(`${t('invoicing.error_pac')}: ${code.slice('pac_error:'.length)}`);
        return on_error(code);
      }
      if (err instanceof Error) on_error(err.message);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="font-display tracking-tight">
            {t('invoicing.request_title')}
          </CardTitle>
          <p className="text-xs text-muted-foreground">{t('invoicing.request_hint')}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading_profiles ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : profiles.length === 0 ? (
            <div className="rounded-md border border-warning/30 bg-warning/15 p-3 text-sm text-warning">
              {t('invoicing.no_fiscal_profiles')}
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <Label>{t('invoicing.fiscal_profile')}</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={profile_id}
                  onChange={(e) => setProfileId(e.target.value)}
                  disabled={submitting}
                >
                  {profiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label ? `${p.label} — ` : ''}
                      {p.legal_name} ({p.rfc})
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label>{t('invoicing.cfdi_usage_override')}</Label>
                <Input
                  value={cfdi_usage_override}
                  onChange={(e) => setCfdiUsageOverride(e.target.value.toUpperCase())}
                  disabled={submitting}
                  placeholder={COMMON_USES.join(' / ')}
                  maxLength={6}
                />
                <p className="text-xs text-muted-foreground">
                  {t('invoicing.cfdi_usage_override_hint')}
                </p>
              </div>
            </>
          )}
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {submitting ? t('common.loading') : t('invoicing.request_confirm')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
